// Web addresses in anyone's words, found so they can open with a click (Features 2, 21 Oct): a note, a deal's
// history, a post, a task, a cell. Words are split into text and links; only http and https ever become a link,
// so "javascript:" or "data:" stays text whatever is written. The rules are GitHub's for a bare address: it starts
// with http://, https:// or www. (which opens as https), and the punctuation that ends a sentence is left out, as
// is a closing bracket the address did not open.
//
// A link shows a short name, never the whole address (Round 7, 21 Oct): its own words when it is written with them,
// as Markdown writes a link ([Arageek article](https://…)), else a short name for the address (linkLabel). The
// words stay exactly as written, for agents, editing and copying; only what is drawn changes.

export interface LinkPart {
  /** the words as written: joined back, the parts are the words exactly */
  text: string;
  /** set on a link only: the address it opens, always http or https */
  href?: string;
  /** set on a link only: what it shows, its own words or a short name for its address (Round 7) */
  label?: string;
}

/** Where an address may start; what follows runs to the next space or a character an address never holds. */
const START = /(?:https?:\/\/|www\.)[^\s<>"]+/gi;
/** Said before an address that is really part of a longer word, like "xhttps://" or "foo.www.example.com". */
const GLUED = /[\p{L}\p{N}@./\\-]/u;
/** What ends a sentence or a quote rather than an address. */
const TRAILING = /[.,:;!?'"*_~‘’“”»›…、。，；：！？]$/u;
const CLOSES: Record<string, string> = { ")": "(", "]": "[", "}": "{", "）": "（", "】": "【", "」": "「", "』": "『" };
/** www. then a name with at least one dot in it. */
const WWW = /^www\.[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+/iu;
/** A link written with its own words, as Markdown writes one: [Arageek article](https://…). Brackets the address opened itself stay in it. */
const NAMED = /\[([^[\]\n]{1,200})\]\(\s*((?:https?:\/\/|www\.)(?:[^\s()<>"]|\([^\s()<>"]*\))+)\s*\)/gi;
/** Words that read as an address themselves: "paypal.com", "www.example.com/page". */
const ADDRESSY = /^(?:https?:\/\/)?(?:www\.)?(?:[\p{L}\p{N}-]+\.)+\p{L}{2,}(?:[/?#:]\S*)?$/iu;
/** How long a short name runs before it is cut: "en.arageek.com/contextbolt-making-ai…" fits. */
const LABEL_MAX = 40;
/** Where a short name may be cut, so a word is never cut in half. */
const BREAKS = "-_/.?=&#+";

const count = (s: string, c: string) => s.split(c).length - 1;

/** Whether the address found at a place in the words is glued to the word before it, and so not an address. */
export const gluedAt = (text: string, at: number) => at > 0 && GLUED.test(text[at - 1]);

/** The address as written, without what ends the sentence around it. */
function trim(raw: string): string {
  let s = raw;
  for (;;) {
    const last = s.slice(-1);
    if (TRAILING.test(last)) s = s.slice(0, -1);
    else if (CLOSES[last] && count(s, last) > count(s, CLOSES[last])) s = s.slice(0, -1);
    else return s;
  }
}

/** The address a link opens, or null when the words are not a web address. */
function hrefOf(s: string): string | null {
  const www = /^www\./i.test(s);
  if (www && !WWW.test(s)) return null;
  try {
    const u = new URL(www ? `https://${s}` : s);
    if (u.protocol !== "https:" && u.protocol !== "http:") return null;
    if (!/[\p{L}\p{N}]/u.test(u.hostname)) return null;
    return u.href;
  } catch {
    return null;
  }
}

/** An address's site, without www., or null when it has none. */
function siteOf(s: string): string | null {
  try {
    return new URL(/^https?:\/\//i.test(s) ? s : `https://${s}`).hostname.replace(/^www\./, "") || null;
  } catch {
    return null;
  }
}

/** Addresses that are all ids past the site, named for what they open; null for any other. */
function namedSite(href: string): string | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  const site = u.hostname.replace(/^www\./, "");
  // a thread's id ends Gmail's address: #inbox/FMfcgzQXJWDsKBGpqRXbZvKxNnWkcZqT
  if (site === "mail.google.com") return /[A-Za-z0-9]{16,}/.test(u.hash) ? "Email in Gmail" : "Gmail";
  if (site === "docs.google.com") {
    const kind = /\/(document|spreadsheets|presentation|forms)\//.exec(u.pathname)?.[1];
    return kind === "document" ? "Google Doc" : kind === "spreadsheets" ? "Google Sheet" : kind === "presentation" ? "Google Slides" : kind === "forms" ? "Google Form" : "Google Docs";
  }
  if (site === "drive.google.com") return "Google Drive";
  return null;
}

/**
 * A short name for a bare address (Round 7): what it opens, where the address is only ids (Gmail, a Google Doc),
 * else its site and the start of its path, cut at a break with an ellipsis: "en.arageek.com/contextbolt-making-ai…".
 * Only the address itself is read: nothing is fetched to learn a page's title. written is the address as it was
 * written, which keeps its own letters; href is where it opens.
 */
export function linkLabel(written: string, href: string = written): string {
  const named = namedSite(href);
  if (named) return named;
  let s = written.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  try {
    s = decodeURI(s);
  } catch {
    // a broken escape stays as written
  }
  // no character that hides or turns words round once decoded (a right-to-left mark)
  s = s.replace(/[\p{Cc}\p{Cf}]/gu, "").replace(/\/+$/, "");
  // the site it really opens always shows: a name before an @ is dropped, and a site too long to show whole keeps its
  // end, where the site's own name is (review, Round 7: "paypal.com:…@evil.example" showed paypal.com)
  const real = siteOf(href);
  if (real) {
    const end = s.search(/[/?#]|$/);
    if (s.slice(0, end).includes("@")) s = real + s.slice(end);
    if (real.length > LABEL_MAX) return `…${real.slice(-(LABEL_MAX - 1))}`;
  }
  if (s.length <= LABEL_MAX) return s;
  let cut = s.slice(0, LABEL_MAX);
  const site = s.indexOf("/");
  const at = Math.max(...[...BREAKS].map((c) => cut.lastIndexOf(c)));
  if (at > site && at >= LABEL_MAX * 0.6) cut = cut.slice(0, at);
  return `${cut.replace(/[-_/.?=&#+:]+$/, "")}…`;
}

/** The names the board itself gives addresses that are only ids (namedSite): words a link may not borrow. */
const BOARD_NAMES = /^(?:email in gmail|gmail|google (?:docs?|sheets?|slides|forms?|drive))$/i;

/**
 * What a link written with its own words shows: those words, unless they read as an address, hold one, or borrow a
 * name the board gives an address, in which case the short name of the address it really opens shows:
 * "[paypal.com](https://evil.example)" shows evil.example. Characters that draw as nothing, or turn words round, are
 * taken out before the words are judged, so "paypal\u200b.com" is read as the address it looks like (review, Round 7).
 */
export function ownWords(said: string, href: string, written: string): string {
  const w = said.replace(/\s+/g, " ").replace(/[\p{Cc}\p{Cf}]/gu, "").trim();
  // its bare letters, as an address would be read: no marks over them, no stop or bracket after
  const skel = w.replace(/\p{M}/gu, "").replace(/[.,;:!?'")\]]+$/u, "");
  const borrowed = BOARD_NAMES.test(w) && namedSite(href)?.toLowerCase() !== w.toLowerCase();
  // words that are an address always show the real address's short name: of another site they would mislead, and of
  // the same site the short name says it better than the whole address (David: never the whole address); words that
  // hold another site's address among others show where it really goes
  if (!w || borrowed || ADDRESSY.test(skel) || linkParts(w).some((p) => p.href && siteOf(p.href) !== siteOf(href))) return linkLabel(written, href);
  return w;
}

/** The words as a link draws them, each address by its short name: for words shown plain, on a card that is a button (Round 7). */
export const shownWords = (text: string): string => linkParts(text).map((p) => p.label ?? p.text).join("");

/** Whether any web address is in the words: cheap enough to ask on every draw. */
export function hasLink(text: string | null | undefined): boolean {
  if (!text || !/https?:\/\/|www\./i.test(text)) return false;
  return linkParts(text).some((p) => p.href);
}

/** Whether the words are one web address and nothing else: a press anywhere on them would be the link's (Round 7). */
export function onlyLink(text: string | null | undefined): boolean {
  const parts = linkParts(text).filter((p) => p.href || p.text.trim());
  return parts.length === 1 && !!parts[0].href;
}

/** The words split into text and links, in order; joined back, the parts' text is the words exactly. */
export function linkParts(text: string | null | undefined): LinkPart[] {
  if (!text) return [];
  if (!/https?:\/\/|www\./i.test(text)) return [{ text }];
  const out: LinkPart[] = [];
  const words = (s: string) => {
    if (!s) return;
    const prev = out.at(-1);
    if (prev && !prev.href) prev.text += s;
    else out.push({ text: s });
  };
  // the bare addresses in a stretch of the words, each with its short name
  const bare = (from: number, to: number) => {
    const part = text.slice(from, to);
    let last = 0;
    START.lastIndex = 0;
    for (let m = START.exec(part); m; m = START.exec(part)) {
      const at = m.index;
      if (gluedAt(text, from + at)) continue;
      const url = trim(m[0]);
      const href = url ? hrefOf(url) : null;
      if (!href) continue;
      words(part.slice(last, at));
      out.push({ text: url, href, label: linkLabel(url, href) });
      last = at + url.length;
      // what was trimmed is read again as words, so a closing bracket or full stop stays where it was
      START.lastIndex = last;
    }
    words(part.slice(last));
  };
  // a link written with its own words is taken whole first (Round 7), and the words between are read for bare ones;
  // all found at once, since reading a link's own words reads words again
  let last = 0;
  for (const m of [...text.matchAll(NAMED)]) {
    const href = hrefOf(m[2]);
    if (!href) continue;
    bare(last, m.index);
    out.push({ text: m[0], href, label: ownWords(m[1], href, m[2]) });
    last = m.index + m[0].length;
  }
  bare(last, text.length);
  return out;
}
